import React from 'react';
import Tooltip from '@atlaskit/tooltip';
import Spinner from '@atlaskit/spinner';
import { css, SerializedStyles } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { ButtonProps } from '../index';
import { generateIconAriaLabel } from '../utils/utils';

interface IconButtonProps extends ButtonProps {
  clickHandler: (e: React.MouseEvent<HTMLElement>) => void;
  testId: string;
  showBeforeIcon: React.ReactElement | false;
  iconColor?: string;
}

export const IconButton: React.FC<IconButtonProps> = ({
  clickHandler,
  testId,
  disabled = false,
  selected = false,
  isLoading = false,
  tooltip,
  iconType,
  showBeforeIcon,
  href,
  target,
  iconColor,
}) => {
  const commonProps = {
    tabIndex: 0,
    'aria-label': generateIconAriaLabel(iconType),
    'data-testid': testId,
    css: [
      learningButtonIconStyles['base'],
      selected && learningButtonIconStyles['selected'],
      iconColor && css({ color: iconColor }),
    ],
    onClick: clickHandler,
  };

  const content = (
    <>
      {isLoading && <Spinner testId="spinner" size="small" />}
      {showBeforeIcon}
    </>
  );

  let element;
  if (href && !disabled) {
    element = (
      <a {...commonProps} href={href} target={target} rel={target === '_blank' ? 'noreferrer' : ''}>
        {content}
      </a>
    );
  } else {
    element = (
      <button id="icon-button" {...commonProps} disabled={disabled}>
        {content}
      </button>
    );
  }

  return tooltip ? (
    <Tooltip content={tooltip} testId="tooltip">
      {element}
    </Tooltip>
  ) : (
    element
  );
};

const learningButtonIconStyles = {
  base: css({
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    border: 'none',
    color: token('color.icon'),
    backgroundColor: 'transparent',
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: token('color.background.accent.gray.subtlest.hovered'),
    },

    '&:active': {
      backgroundColor: token('color.background.accent.gray.subtlest.pressed'),
    },

    '&:disabled': {
      backgroundColor: token('color.background.disabled'),
      color: token('color.icon.disabled'),
      cursor: 'not-allowed',
    },
  }),

  selected: css({
    backgroundColor: token('color.background.selected'),
    color: token('color.icon.selected'),

    '&:hover': {
      backgroundColor: token('color.background.selected.hovered'),
      color: token('color.icon.selected'),
    },
  }),
} as { [key: string]: SerializedStyles };
